import { useMemo, useState, useEffect, useCallback, useRef } from 'react'
import {
    Spin,
    Select,
    Space,
    Col,
    Row,
    Form,
    notification,
    Table,
    Typography,
    Tabs,
    Card,
    InputNumber,
    message,
    Radio,
    Empty,
} from "antd";
import { useDispatch, useSelector } from 'react-redux'
import { AsyncStates } from "src/constants"
import { StoreState } from 'src/store/configureStore'
import { LoadingOutlined } from '@ant-design/icons'
import { fetchInterpolationDataClear, fetchInterpolationDataRequest, graphsDataHistoryRequest, plotsDataRequest } from 'src/store/actions/workOrderDetails'
import { Unsubscribe, doc, onSnapshot } from "firebase/firestore";
import { FB_COLLECTION_NAME_TASKERS, firestoreDb } from "src/utils/firebase";
import { convertToPrecision, isValidNumber } from 'src/utils/decorator'
import Highcharts from "highcharts"
import HighchartsReact from "highcharts-react-official"
import { Slider } from 'antd/lib'
import { StyledButton } from 'src/styled_components/StyledButton'
import { useValue } from 'src/utils/useValue';
import useTranslate from 'src/utils/useTranslate';
import Boost from 'highcharts/modules/boost';

Boost(Highcharts);

// For Negative Value Support

(function (H) {
    H.addEvent(H.Axis, 'afterInit', function () {
        const logarithmic = (this as any).logarithmic;
        if (logarithmic && (this.options as any).custom?.allowNegativeLog) {
            (this as any).positiveValuesOnly = false;
            logarithmic.log2lin = (num: any) => {
                const isNeg = num < 0;
                let abs = Math.abs(num);
                if (abs < 10) {
                    abs += (10 - abs) / 10;
                }
                const result = Math.log(abs) / Math.LN10;
                return isNeg ? -result : result;
            };
            logarithmic.lin2log = (num: any) => {
                const isNeg = num < 0;
                let result = Math.pow(10, Math.abs(num));
                if (result < 10) {
                    result = (10 * (result - 1)) / (10 - 1);
                }
                return isNeg ? -result : result;
            };
        }
    });
})(Highcharts);

const { Option } = Select
const { Text } = Typography


type AxisKey = string | null;

type SelectedAxis = {
    xAxis: AxisKey;
    yAxis: AxisKey;
};

export const RawPlots = ({ currentExpIdIndex, componentType }: any) => {
    const dispatch = useDispatch()
    const [t] = useTranslate()
    const { formatter, parser } = useValue()

    const { plotsData, plotsDataStatus, graphsTaskId, workOrder, graphsDataDeleteStatus, interPolationGraphsDataStatus, interPolationGraphsData, graphsDataHistory } = useSelector((state: StoreState) => state.workOrderDetails)
    const configs = useSelector((state: StoreState) => state.configs.features)
    const [selectedTrial, setSelectedTrial] = useState("")
    const [points, setPoints] = useState<any>({})
    const [superimposed, setSuperimposed] = useState(false)
    const [xaxis, setXaxis] = useState<any[]>([])
    const [yaxis, setYaxis] = useState<any[]>([])
    const [range, setRange] = useState<any>({})
    const [tab, setTab] = useState("generalised_dsc")
    const [inputCount, setInputCount] = useState<number | null>(2);
    const [interpolationForm] = Form.useForm();
    const trialSetList = useMemo(() => (Object.keys(plotsData?.[tab] || {})), [plotsData, tab])
    const [selectedAxis, setSelectedAxis] = useState<SelectedAxis>({
        xAxis: null,
        yAxis: null,
    });

    const interpolationGraphConfigData = useMemo(() => {
        const configData = graphsDataHistory
            .filter((graphData: any) => graphData?.type === tab)
            .reduce((acc: Record<string, { min: number; max: number; unit: string }>, curr: any) => {
                const configs = curr?.configs ?? {};

                Object.keys(configs).forEach((key) => {
                    const currentConfig = configs[key];
                    const existing = acc[key];

                    const min = existing ? Math.min(existing.min, currentConfig.min) : currentConfig.min;
                    const max = existing ? Math.max(existing.max, currentConfig.max) : currentConfig.max;
                    const units = new Set([
                        ...(existing?.unit?.split(',').map((u) => u.trim()) ?? []),
                        currentConfig?.unit?.trim(),
                    ])

                    acc[key] = {
                        min,
                        max,
                        unit: [...units].filter(Boolean).join(', ')
                    };
                });

                return acc;
            }, {});

        return configData;
    }, [graphsDataHistory, tab]);


    const interpolationMinMaxData = useMemo(() => {
        if (!selectedAxis.xAxis) {
            return {
                min: 0,
                max: 0
            }
        }

        return {
            min: interpolationGraphConfigData?.[selectedAxis.xAxis]?.min ?? 0,
            max: interpolationGraphConfigData?.[selectedAxis.xAxis]?.max ?? 0
        }
    }, [interpolationGraphConfigData, selectedAxis.xAxis])

    const interpolationOptions = useMemo(() => {
        const keys = graphsDataHistory.filter((g: any) => g.type === tab).flatMap((graph: any) => Object.keys(graph.data ?? {}))
        const uniqueKeys = [...new Set(keys)];

        return uniqueKeys.filter(
            (k) => k !== selectedAxis.xAxis && k !== selectedAxis.yAxis
        );
    }, [graphsDataHistory, tab, selectedAxis.xAxis, selectedAxis.yAxis]);

    // graphs file upload
    useEffect(() => {
        let unsub: Unsubscribe
        const listenToFileUploadTaskStatus = async () => {
            const taskDocRef = doc(firestoreDb, `${FB_COLLECTION_NAME_TASKERS}/${graphsTaskId}`)
            unsub = onSnapshot(taskDocRef, (doc: any) => {
                const taskDoc = doc.data()
                if (taskDoc) {
                    const taskStatus = taskDoc?.status
                    if (taskStatus === "COMPLETED") {
                        notification.success({ message: "Graphs generated succesfully" })
                        dispatch(plotsDataRequest({ work_order_id: workOrder.work_order_id, experiment_id: workOrder?.experiment_id?.[currentExpIdIndex] }))
                        dispatch(graphsDataHistoryRequest({ work_order_id: workOrder.work_order_id, experiment_id: workOrder?.experiment_id?.[currentExpIdIndex] }))
                        unsub()
                    }
                    if (taskStatus === "FAILED") {
                        notification.error({ message: "Added columns not found in the file. Failed to generate graphs" })
                        unsub()
                    }
                }
            })
        }
        if (graphsTaskId) {
            listenToFileUploadTaskStatus()
        }
    }, [dispatch, graphsTaskId, workOrder?.work_order_id, workOrder?.experiment_id, currentExpIdIndex])

    useEffect(() => {
        if (plotsDataStatus === AsyncStates.SUCCESS) {
            if (Object.keys(plotsData?.[tab] || {}).length) {
                setSelectedTrial(Object.keys(plotsData?.[tab] ?? {})?.[0])
            } else {
                setSelectedTrial("")
            }
            setXaxis([])
            setYaxis([])
        }
    }, [plotsDataStatus, plotsData, tab])

    const graphsClickEvent = useCallback((e: any, key: string) => {
        const point = e.point
        if (tab === "generalised_dsc") {
            setPoints((prevState: any) => {
                const obj = prevState?.[key]
                const selectedCycle = point?.series?.name
                const selectedPoint = obj?.selectedPoint
                obj.cycles[selectedCycle][selectedPoint ?? "onset"] = JSON.stringify({ x: convertToPrecision(point.x), y: convertToPrecision(point.y) })
                return ({ ...prevState, [key]: obj })
            })
        } else {
            setPoints((prevState: any) => {
                const obj = prevState?.[key]
                const selectedPoint = obj?.selectedPoint
                obj[selectedPoint ?? "onset"] = JSON.stringify({ x: convertToPrecision(point.x), y: convertToPrecision(point.y) })
                return ({ ...prevState, [key]: obj })
            })
        }
    }, [tab])

    const resetCalculationTable = useCallback((key: string) => {
        if (tab === "generalised_dsc") {
            const cycles = Object.keys(plotsData?.[tab]?.[selectedTrial]?.data || {})
            setPoints((prevState: any) => ({
                ...prevState, [key]: {
                    cycles: cycles.reduce((obj, cycle: string) => ({
                        ...obj, [cycle]: { onset: "-", peak: "-", end: "-", cycleName: cycle }
                    }), {}),
                    selectedPoint: "onset"
                }
            }))
        } else {
            setPoints((prevState: any) => ({ ...prevState, [key]: { onset: "-", peak: "-", end: "-", selectedPoint: "onset" } }))
        }
    }, [tab, plotsData, selectedTrial])

    const getCalculationTable = useCallback((key: string) => {
        const columns: any = []
        if (tab === "generalised_dsc") {
            columns.push({
                dataIndex: 'cycleName',
                title: 'Cycles',
                key: 'cycles',
                align: "center",
                width: 50
            })
        }
        columns.push(
            {
                dataIndex: 'onset',
                title: 'Onset',
                key: 'onset',
                align: "center",
                width: 80
            },
            {
                dataIndex: 'peak',
                title: 'Peak',
                key: 'peak',
                align: "center",
                width: 80
            },
            {
                dataIndex: 'end',
                title: "End",
                key: 'end',
                align: "center",
                width: 80
            }
        )
        let dataSource = []
        if (tab === "generalised_dsc") {
            dataSource = Object.values(points?.[key]?.cycles || {}) || []
        } else {
            dataSource = [points?.[key]]
        }

        return (
            <Space direction="vertical" size="large" style={{ width: "100%" }} id="points-table">
                <Table
                    title={() => (
                        <Space>
                            <Text strong>{"Select Point - "}</Text>
                            <Select value={points?.[key]?.selectedPoint} style={{ width: 200 }}
                                onChange={(e) => setPoints((prevState: any) => ({ ...prevState, [key]: { ...prevState?.[key], selectedPoint: e } }))}>
                                <Option value={"onset"} key={"onset"}>{"Onset"}</Option>
                                <Option value={"peak"} key={"peak"}>{"Peak"}</Option>
                                <Option value={"end"} key={"end"}>{"End"}</Option>
                            </Select>
                        </Space>
                    )}
                    columns={columns} dataSource={dataSource} pagination={false} style={{ marginTop: 20 }} />
                <Row justify="end">
                    <Space size="large">
                        <StyledButton type="dashed"
                            onClick={() => resetCalculationTable(key)}>
                            {"Reset"}
                        </StyledButton>
                    </Space>
                </Row>
            </Space>
        )
    }, [points, tab, resetCalculationTable])

    useEffect(() => {
        if (superimposed) {
            setRange((prevState: any) => {
                return xaxis.reduce((obj: any, element: any) => (
                    { ...obj, [element]: prevState?.[element] || { x: { min: "", max: "" }, y: { min: "", max: "" } } }
                ), {})
            })
            setPoints((prevState: any) => {
                return xaxis.reduce((obj: any, element: any) => (
                    { ...obj, [element]: prevState?.[element] || [{ onset: "-", peak: "-", end: "-", selectedPoint: "onset" }] }
                ), {})
            })
        } else {
            setRange((prevState: any) => {
                return xaxis.flatMap((x_res: any) => yaxis.map((y_res: any) => x_res + y_res))
                    .reduce((obj: any, element: any) => (
                        { ...obj, [element]: prevState?.[element] || { x: { min: "", max: "" }, y: { min: "", max: "" } } }
                    ), {})
            })
            if (tab === "generalised_dsc") {
                const cycles = Object.keys(plotsData?.[tab]?.[selectedTrial]?.data || {})
                setPoints((prevState: any) => {
                    return xaxis.flatMap((x_res: any) => yaxis.map((y_res: any) => x_res + y_res))
                        .reduce((obj: any, element: any) => (
                            {
                                ...obj, [element]: prevState?.[element] || {
                                    cycles: cycles.reduce((obj, cycle: string) => ({
                                        ...obj, [cycle]: { onset: "-", peak: "-", end: "-", cycleName: cycle }
                                    }), {}),
                                    selectedPoint: "onset"
                                }
                            }
                        ), {})
                })
            } else {
                setPoints((prevState: any) => {
                    return xaxis.flatMap((x_res: any) => yaxis.map((y_res: any) => x_res + y_res))
                        .reduce((obj: any, element: any) => (
                            { ...obj, [element]: prevState?.[element] || { onset: "-", peak: "-", end: "-", selectedPoint: "onset" } }
                        ), {})
                })
            }
        }
    }, [superimposed, xaxis, yaxis, tab, plotsData, selectedTrial])


    const generateValues = useCallback((count) => {
        const xAxis = interpolationForm.getFieldValue("x")
        const interpolationMinMaxData = graphsDataHistory.find((graphData: any) => graphData?.type === tab)?.configs?.[xAxis]

        const min = interpolationMinMaxData?.min ?? 0
        const max = interpolationMinMaxData?.max ?? 0

        if (count === 1) {
            return [min];
        }
        if (count === 0) {
            return [];
        }

        const stepSize = (max - min) / (count - 1);
        const data = Array.from({ length: count }, (_, i) => min + stepSize * i);
        return data
    }, [graphsDataHistory, interpolationForm, tab]);

    const handleCountChange = useCallback((value: string) => {
        if (value === "") {
            setInputCount(2);
            interpolationForm.setFieldsValue({
                target_temperature_count: 2,
                data_values: generateValues(2).map((value, index) => ({ [`target_temperature_${index}`]: value }))
            });

            return;
        }
        const inputNumber = Number(value);
        if (inputNumber >= 0 && inputNumber <= 10) {

            interpolationForm.setFieldsValue({
                target_temperature_count: inputNumber,
                data_values: generateValues(inputNumber).map((value, index) => ({ [`target_temperature_${index}`]: value }))
            });


            setInputCount(inputNumber);
        } else {
            interpolationForm.setFieldValue('target_temperature_count', inputCount);
            message.error('Number should be between 0 and 10');
        }
    }, [interpolationForm, generateValues, inputCount])

    useEffect(() => {
        if (tab === "interpolation") {
            const data_values = interpolationForm.getFieldValue("data_values")
            if (!data_values?.length) {
                interpolationForm.setFieldValue("data_values", generateValues(2).map((value, index) => ({ [`target_temperature_${index}`]: value })))
            }
        }
    }, [interpolationForm, tab, generateValues])

    const handleSubmit = useCallback((values: any) => {
        if (!interpolationGraphConfigData) return
        const payload = {
            work_order_id: workOrder.work_order_id,
            data_values: values.data_values.map((temperature: any) => Object.values(temperature)?.[0]),
            x: selectedAxis.xAxis,
            y: selectedAxis.yAxis
        }
        dispatch(fetchInterpolationDataRequest(payload))
    }, [interpolationGraphConfigData, workOrder.work_order_id, selectedAxis.xAxis, selectedAxis.yAxis, dispatch])


    const getPlotsData = useCallback(() => {
        let xAxisOptions: any = [], yAxisOptions: any = []
        if (tab === "generalised_dsc") {
            xAxisOptions = [...new Set(Object.entries(plotsData?.[tab]?.[selectedTrial]?.data || {})
                .flatMap(([key, value]: any) => Object.keys(value)))]
                .filter((res: any) => !yaxis.includes(res))
                .map((res: any) => (
                    <Option key={res} value={res}>{res}</Option>
                ))
            yAxisOptions = [...new Set(Object.entries(plotsData?.[tab]?.[selectedTrial]?.data || {})
                .flatMap(([key, value]: any) => Object.keys(value)))]
                .filter((res: any) => !xaxis.includes(res))
                .map((res: any) => (
                    <Option key={res} value={res}>{res}</Option>
                ))
        } else {
            xAxisOptions = Object.keys(plotsData?.[tab]?.[selectedTrial]?.data || {})
                .filter((res: any) => !yaxis.includes(res))
                .map((res: any) => (
                    <Option key={res} value={res}>{res}</Option>
                ))
            yAxisOptions = Object.keys(plotsData?.[tab]?.[selectedTrial]?.data || {})
                .filter((res: any) => !xaxis.includes(res))
                .map((res: any) => (
                    <Option key={res} value={res}>{res}</Option>
                ))
        }

        return (
            <Space direction="vertical" size="large" style={{ width: "100%", marginTop: 20 }} key={tab}>
                {
                    tab === "interpolation" ?
                        <>
                            <Form
                                form={interpolationForm}
                                autoComplete="off"
                                layout='vertical'
                                initialValues={{ target_temperature_count: inputCount, data_values: [] }}
                                onFinish={handleSubmit}
                            >
                                <div style={{
                                    display: "flex",
                                    gap: "1rem",
                                    width: "100%"
                                }}>
                                    <Form.Item
                                        style={{
                                            width: "100%"
                                        }}
                                        name="x" label={t("common.selectXAxis")}
                                        required={true}
                                        rules={[{ required: true, message: "required" }]}
                                    >
                                        <Select
                                            style={{
                                                width: "100%"
                                            }}
                                            allowClear
                                            showSearch
                                            optionFilterProp="children"
                                            onChange={(value) => {
                                                setSelectedAxis((prev) => ({
                                                    ...prev,
                                                    xAxis: value
                                                }))
                                                interpolationForm.setFieldsValue({
                                                    temperature_key: value,
                                                    target_temperature_count: 2,
                                                    data_values: generateValues(2).map((value, index) => ({ [`target_temperature_${index}`]: value }))
                                                })
                                            }}
                                        >
                                            {interpolationOptions.map((res: any) => (
                                                <Option value={res} key={res}>{res}</Option>
                                            ))}
                                        </Select>
                                    </Form.Item>

                                    <Form.Item
                                        style={{
                                            width: "100%"
                                        }}
                                        name="y" label={t("common.selectYAxis")}
                                        required={true}
                                        rules={[{ required: true, message: "required" }]}
                                    >
                                        <Select
                                            showSearch
                                            style={{
                                                width: "100%"
                                            }}
                                            allowClear
                                            optionFilterProp="children"
                                            onChange={(value) => {
                                                setSelectedAxis((prev) => ({
                                                    ...prev,
                                                    yAxis: value
                                                }))
                                            }}
                                        >
                                            {interpolationOptions.map((res: any) => (
                                                <Option value={res} key={res}>{res}</Option>
                                            ))}
                                        </Select>
                                    </Form.Item>
                                </div>

                                {
                                    !!selectedAxis.xAxis && <>
                                        <Form.Item
                                            style={{
                                                maxWidth: "50%"
                                            }}
                                            name="target_temperature_count"
                                            label={`Number of target ${selectedAxis.xAxis ?? ""}`}
                                            rules={[{ required: true, message: 'Please enter a number between 0 and 10' }]}
                                        >
                                            <InputNumber
                                                formatter={formatter}
                                                parser={parser}
                                                min={0} max={10}
                                                onBlur={(e) => handleCountChange(e.target.value)} style={{ width: '100%' }} />
                                        </Form.Item>

                                        <Form.Item>
                                            <Form.List name={'data_values'}>
                                                {(subFields, subOpt) => (
                                                    <div style={{
                                                        display: "flex",
                                                        gap: "1rem",
                                                        overflowX: "scroll",
                                                        justifyContent: "space-between",
                                                        width: "100%"
                                                    }}>
                                                        {subFields.map((subField) => (
                                                            <Form.Item key={subField.name} name={[subField.name, `target_temperature_${subField.name}`]}
                                                                shouldUpdate
                                                                style={{
                                                                    width: "100%",
                                                                }}
                                                                label={`${selectedAxis.xAxis} ${Number(subField.name) + 1}` + (interpolationGraphConfigData?.[selectedAxis.xAxis as string]?.unit ? ` ${interpolationGraphConfigData[selectedAxis.xAxis as string].unit}` : '')}
                                                                rules={[
                                                                    { required: true, message: 'This field is required' },
                                                                    { type: 'number', min: interpolationMinMaxData?.min, max: interpolationMinMaxData?.max, message: `Must be a number between ${interpolationMinMaxData?.min} and ${interpolationMinMaxData?.max}` }
                                                                ]}
                                                            >
                                                                <InputNumber
                                                                    formatter={formatter}
                                                                    parser={parser}
                                                                    style={{ width: '100%' }}
                                                                    min={interpolationMinMaxData?.min} max={interpolationMinMaxData?.max}
                                                                />
                                                            </Form.Item>
                                                        ))}
                                                    </div>
                                                )}
                                            </Form.List>
                                        </Form.Item>
                                    </>
                                }
                                <Form.Item>
                                    <div
                                        style={{
                                            display: "flex",
                                            justifyContent: "flex-end",
                                        }}
                                    >
                                        <StyledButton type="primary" htmlType="submit" loading={interPolationGraphsDataStatus === AsyncStates.LOADING}>
                                            {"Perform Interpolation Analysis"}
                                        </StyledButton>
                                    </div>
                                </Form.Item>
                            </Form>
                            {!!interPolationGraphsData?.results?.length && (
                                <>
                                    {interPolationGraphsData.results.map((result: any) => {
                                        const displayName = result.display_id;
                                        const plotData = result.plots ?? {};

                                        return (
                                            <div key={result?.formulation_id}
                                                style={{ display: 'flex', flexDirection: 'column', gap: "0.5rem", border: "1px solid #ccc", padding: "1rem", borderRadius: "0.5rem" }}
                                            >
                                                <Typography.Title level={3}>{displayName}</Typography.Title>
                                                {Object.keys(plotData).map((key: string) => {
                                                    const interpolationDataRelatedToProperty = plotData[key];
                                                    const plotKey = `${result?.formulation_id}_________${key}`

                                                    return <Spin spinning={interPolationGraphsDataStatus === AsyncStates.LOADING} indicator={<LoadingOutlined />}>
                                                        <InterpolationChart data={interpolationDataRelatedToProperty} plotKey={plotKey} key={plotKey} xAxisName={result?.x_axis} yAxisName={result?.y_axis} />
                                                    </Spin>
                                                })}
                                            </div>
                                        );
                                    })}
                                </>
                            )}
                        </>
                        : <>
                            <Col span={12}>
                                <Form layout='vertical'>
                                    <Form.Item label={t("common.selectTrial")}>
                                        <Select value={selectedTrial} style={{ width: "100%" }} notFoundContent={<Empty description={t("common.noData")}/>} onSelect={(e: any) => {
                                            setSuperimposed(false)
                                            setXaxis([])
                                            setYaxis([])
                                            setSelectedTrial(e)
                                        }}>
                                            {trialSetList.map(res => (
                                                <Select.Option value={res}>{res}</Select.Option>
                                            ))}
                                        </Select>
                                    </Form.Item>
                                </Form>
                            </Col>
                            <Space>
                                <Select placeholder={t("common.selectXAxis")} notFoundContent={<Empty description={t("common.noData")}/>} style={{ minWidth: 300 }} mode="multiple" value={xaxis}
                                    onChange={(e) => setXaxis(e)}>
                                    {xAxisOptions}
                                </Select>
                                <Select placeholder={t("common.selectYAxis")} notFoundContent={<Empty description={t("common.noData")}/>} style={{ minWidth: 300 }} mode="multiple" value={yaxis}
                                    onChange={(e: any) => setYaxis(e)}>
                                    {yAxisOptions}
                                </Select>
                            </Space>
                            <Space direction="vertical" size="large" style={{ width: '100%' }}>
                                {((tab === "generalised_dma" || tab === "generalised_gpc") && yaxis.length > 1 && xaxis.length > 0) &&
                                    <StyledButton onClick={() => setSuperimposed(prevState => !prevState)} type="primary">
                                        {superimposed ? "Create Separate Graphs" : "Superimpose Graphs"}
                                    </StyledButton>
                                }
                                {tab === "generalised_dsc" ?
                                    (xaxis.map((x_res: any, x_index: any) => {
                                        const x: any = Object.entries(plotsData?.[tab]?.[selectedTrial]?.data || {})
                                            .reduce((obj, [key, value]: any) => ({
                                                ...obj, [key]: value?.[x_res]
                                                    ?.map((point: any) => isValidNumber(point) ? point : null) || []
                                            }), {})
                                        const xMin = Math.min(...(Object.values(x).flatMap((res: any) => res)))
                                        const xMax = Math.max(...(Object.values(x).flatMap((res: any) => res)))
                                        return (
                                            yaxis.map((y_res: any, y_index: number) => {
                                                const currentRange = range?.[x_res + y_res]
                                                const y: any = Object.entries(plotsData?.[tab]?.[selectedTrial]?.data || {})
                                                    .reduce((obj, [key, value]: any) => ({
                                                        ...obj, [key]: value?.[y_res]
                                                            ?.map((point: any) => isValidNumber(point) ? point : null) || []
                                                    }), {})
                                                const yMin = Math.min(...(Object.values(y).flatMap((res: any) => res)))
                                                const yMax = Math.max(...(Object.values(y).flatMap((res: any) => res)))
                                                return (
                                                    <Col span={16}>
                                                        <Card>
                                                            <HighchartsReact
                                                                style={{ width: "100%" }}
                                                                key={"separate" + x_index + y_index}
                                                                highcharts={Highcharts}
                                                                options={{
                                                                    plotOptions: {
                                                                        series: {
                                                                            point: {
                                                                                events: {
                                                                                    click(e: any) { graphsClickEvent(e, x_res + y_res) }
                                                                                }
                                                                            }
                                                                        }
                                                                    },
                                                                    credits: {
                                                                        enabled: false,
                                                                    },
                                                                    chart: {
                                                                        type: "scatter",
                                                                        height: "550px",
                                                                        zoomType: "xy"
                                                                    },
                                                                    title: {
                                                                        text: `${x_res ?? ""} vs ${y_res ?? ""}`, style: { fontSize: "1.2em", fontWeight: 'bold' }
                                                                    },
                                                                    legend: { enabled: true, itemStyle: { fontSize: "1em" } },
                                                                    xAxis: {
                                                                        title: {
                                                                            text: x_res,
                                                                            style: { fontSize: "1em", fontWeight: 'bold' }
                                                                        },
                                                                        labels: { style: { fontSize: "1em", fontWeight: 'bold' } },
                                                                        startOnTick: true,
                                                                        endOnTick: true,
                                                                        showLastLabel: true,
                                                                    },
                                                                    yAxis: {
                                                                        title: {
                                                                            text: y_res, style: { fontSize: "1em", fontWeight: 'bold' }
                                                                        },
                                                                        labels: { style: { fontSize: "1em", fontWeight: 'bold' } },
                                                                    },
                                                                    series: Object.entries(x).map(([x_cycle, x_values]: any) => {
                                                                        const currentY = y?.[x_cycle]
                                                                        return (
                                                                            {
                                                                                data: x_values.map((point: number, index: number) => [point, currentY[index]])
                                                                                    .filter(([x_point, y_point]: any[]) => (x_point !== null) &&
                                                                                        (isValidNumber(currentRange?.x?.min) ? currentRange?.x?.min <= x_point : true) &&
                                                                                        (isValidNumber(currentRange?.x?.max) ? currentRange?.x?.max >= x_point : true) &&
                                                                                        (y_point !== null) &&
                                                                                        (isValidNumber(currentRange?.y?.min) ? currentRange?.y?.min <= y_point : true) &&
                                                                                        (isValidNumber(currentRange?.y?.max) ? currentRange?.y?.max >= y_point : true)
                                                                                    ),
                                                                                name: x_cycle,
                                                                                id: x_cycle
                                                                            }
                                                                        )
                                                                    })
                                                                }}
                                                            />
                                                            <Row justify={"space-between"} align={"middle"}>
                                                                <Col span={2}>
                                                                    <Text strong type="secondary">{"X-axis"}</Text>
                                                                </Col>
                                                                <Col span={22}>
                                                                    <Slider
                                                                        key={"separateX"}
                                                                        range
                                                                        min={xMin}
                                                                        max={xMax}
                                                                        defaultValue={[xMin, xMax]}
                                                                        onAfterChange={(e) => {
                                                                            setRange((prevState: any) => ({ ...prevState, [x_res + y_res]: { ...prevState?.[x_res + y_res], x: { min: e?.[0], max: e?.[1] } } }))
                                                                        }}
                                                                    />
                                                                </Col>
                                                            </Row>
                                                            <Row justify={"space-between"} align={"middle"}>
                                                                <Col span={2}>
                                                                    <Text strong type="secondary">{"Y-axis"}</Text>
                                                                </Col>
                                                                <Col span={22}>
                                                                    <Slider
                                                                        key={"separateY"}
                                                                        range
                                                                        min={yMin}
                                                                        max={yMax}
                                                                        defaultValue={[yMin, yMax]}
                                                                        onAfterChange={(e) => {
                                                                            setRange((prevState: any) => ({ ...prevState, [x_res + y_res]: { ...prevState?.[x_res + y_res], y: { min: e?.[0], max: e?.[1] } } }))
                                                                        }}
                                                                    />
                                                                </Col>
                                                            </Row>
                                                            {componentType === "crud" && getCalculationTable(x_res + y_res)}
                                                        </Card>
                                                    </Col>
                                                )
                                            })
                                        )
                                    }))
                                    : (superimposed ?
                                        (xaxis.map((x_res: any, x_index: number) => {
                                            const x = plotsData?.[tab]?.[selectedTrial]?.data?.[x_res]?.map((point: any) => isValidNumber(point) ? point : null) || []
                                            const yAxisFullData = yaxis.flatMap((y_res: any) => {
                                                return plotsData?.[tab]?.[selectedTrial]?.data?.[y_res]?.map((point: any) => isValidNumber(point) ? point : null) || []
                                            })
                                            const currentRange = range?.[x_res]
                                            const xMin = Math.min(...x)
                                            const xMax = Math.max(...x)
                                            const yMin = Math.min(...yAxisFullData)
                                            const yMax = Math.max(...yAxisFullData)
                                            return (
                                                <Col span={16}>
                                                    <Card>
                                                        <HighchartsReact
                                                            style={{ width: "100%" }}
                                                            key={"imposed" + x_index}
                                                            highcharts={Highcharts}
                                                            options={{
                                                                credits: {
                                                                    enabled: false,
                                                                },
                                                                plotOptions: {
                                                                    series: {
                                                                        point: {
                                                                            events: {
                                                                                click(e: any) { graphsClickEvent(e, x_res) }
                                                                            }
                                                                        }
                                                                    },
                                                                    scatter: {
                                                                        jitter: {
                                                                            x: 0.005
                                                                        }
                                                                    }
                                                                },
                                                                chart: {
                                                                    type: "scatter",
                                                                    height: "550px",
                                                                    zoomType: "xy"
                                                                },
                                                                title: {
                                                                    text: !!x_res && !!yaxis?.length ? `${x_res ?? ""} vs ${yaxis.join(", ")}` : "",
                                                                    style: { fontSize: "1.2em", fontWeight: 'bold' }
                                                                },
                                                                legend: { enabled: true, itemStyle: { fontSize: "1em" } },
                                                                xAxis: {
                                                                    title: {
                                                                        text: x_res,
                                                                        style: { fontSize: "1em", fontWeight: 'bold' }
                                                                    },
                                                                    labels: { style: { fontSize: "1em", fontWeight: 'bold' } },
                                                                    startOnTick: true,
                                                                    endOnTick: true,
                                                                    showLastLabel: true,
                                                                },
                                                                yAxis: {
                                                                    labels: { style: { fontSize: "1em", fontWeight: 'bold' } },
                                                                },
                                                                series: yaxis.map((y_res: any) => {
                                                                    const y = plotsData?.[tab]?.[selectedTrial]?.data?.[y_res]?.map((point: any) => isValidNumber(point) ? point : null) || []
                                                                    return (
                                                                        {
                                                                            data: x.map((point: number, index: number) => [point, y[index]])
                                                                                .filter(([x_point, y_point]: any[]) => (x_point !== null) &&
                                                                                    (isValidNumber(currentRange?.x?.min) ? currentRange?.x?.min <= x_point : true) &&
                                                                                    (isValidNumber(currentRange?.x?.max) ? currentRange?.x?.max >= x_point : true) &&
                                                                                    (y_point !== null) &&
                                                                                    (isValidNumber(currentRange?.y?.min) ? currentRange?.y?.min <= y_point : true) &&
                                                                                    (isValidNumber(currentRange?.y?.max) ? currentRange?.y?.max >= y_point : true)
                                                                                ),
                                                                            name: y_res,
                                                                            id: y_res
                                                                        }
                                                                    )
                                                                })
                                                            }}
                                                        />
                                                        <Row justify={"space-between"} align={"middle"}>
                                                            <Col span={2}>
                                                                <Text strong type="secondary">{"X-axis"}</Text>
                                                            </Col>
                                                            <Col span={22}>
                                                                <Slider
                                                                    key={"imposedX"}
                                                                    range
                                                                    min={xMin}
                                                                    max={xMax}
                                                                    defaultValue={[xMin, xMax]}
                                                                    onAfterChange={(e) => {
                                                                        setRange((prevState: any) => ({ ...prevState, [x_res]: { ...prevState?.[x_res], x: { min: e?.[0], max: e?.[1] } } }))
                                                                    }}
                                                                />
                                                            </Col>
                                                        </Row>
                                                        <Row justify={"space-between"} align={"middle"}>
                                                            <Col span={2}>
                                                                <Text strong type="secondary">{"Y-axis"}</Text>
                                                            </Col>
                                                            <Col span={22}>
                                                                <Slider
                                                                    key={"imposedY"}
                                                                    range
                                                                    min={yMin}
                                                                    max={yMax}
                                                                    defaultValue={[yMin, yMax]}
                                                                    onAfterChange={(e) => {
                                                                        setRange((prevState: any) => ({ ...prevState, [x_res]: { ...prevState?.[x_res], y: { min: e?.[0], max: e?.[1] } } }))
                                                                    }}
                                                                />
                                                            </Col>
                                                        </Row>
                                                        {componentType === "crud" && getCalculationTable(x_res)}
                                                    </Card>
                                                </Col>
                                            )
                                        }))
                                        :
                                        (xaxis.map((x_res: any, x_index: any) => {
                                            const x = plotsData?.[tab]?.[selectedTrial]?.data?.[x_res]?.map((point: any) => isValidNumber(point) ? point : null) || []
                                            const xMin = Math.min(...x)
                                            const xMax = Math.max(...x)
                                            return (
                                                yaxis.map((y_res: any, y_index: number) => {
                                                    const currentRange = range?.[x_res + y_res]
                                                    const y = plotsData?.[tab]?.[selectedTrial]?.data?.[y_res]?.map((point: any) => isValidNumber(point) ? point : null) || []
                                                    const yMin = Math.min(...y)
                                                    const yMax = Math.max(...y)
                                                    return (
                                                        <Col span={16}>
                                                            <Card>
                                                                <HighchartsReact
                                                                    style={{ width: "100%" }}
                                                                    key={"separate" + x_index + y_index}
                                                                    highcharts={Highcharts}
                                                                    options={{
                                                                        credits: {
                                                                            enabled: false,
                                                                        },
                                                                        plotOptions: {
                                                                            series: {
                                                                                point: {
                                                                                    events: {
                                                                                        click(e: any) { graphsClickEvent(e, x_res + y_res) }
                                                                                    }
                                                                                }
                                                                            }
                                                                        },
                                                                        chart: {
                                                                            type: "scatter",
                                                                            height: "550px",
                                                                            zoomType: "xy"
                                                                        },
                                                                        title: {
                                                                            text: `${x_res ?? ""} vs ${y_res ?? ""}`, style: { fontSize: "1.2em", fontWeight: 'bold' }
                                                                        },
                                                                        legend: { enabled: true, itemStyle: { fontSize: "1em" } },
                                                                        xAxis: {
                                                                            title: {
                                                                                text: x_res,
                                                                                style: { fontSize: "1em", fontWeight: 'bold' }
                                                                            },
                                                                            labels: { style: { fontSize: "1em", fontWeight: 'bold' } },
                                                                            startOnTick: true,
                                                                            endOnTick: true,
                                                                            showLastLabel: true,
                                                                        },
                                                                        yAxis: {
                                                                            title: {
                                                                                text: y_res, style: { fontSize: "1em", fontWeight: 'bold' }
                                                                            },
                                                                            labels: { style: { fontSize: "1em", fontWeight: 'bold' } },
                                                                        },
                                                                        series: [{
                                                                            data: x.map((point: number, index: number) => [point, y[index]])
                                                                                .filter(([x_point, y_point]: any[]) => (x_point !== null) &&
                                                                                    (isValidNumber(currentRange?.x?.min) ? currentRange?.x?.min <= x_point : true) &&
                                                                                    (isValidNumber(currentRange?.x?.max) ? currentRange?.x?.max >= x_point : true) &&
                                                                                    (y_point !== null) &&
                                                                                    (isValidNumber(currentRange?.y?.min) ? currentRange?.y?.min <= y_point : true) &&
                                                                                    (isValidNumber(currentRange?.y?.max) ? currentRange?.y?.max >= y_point : true)
                                                                                ),
                                                                            name: y_res,
                                                                            id: y_res
                                                                        }]
                                                                    }}
                                                                />
                                                                <Row justify={"space-between"} align={"middle"}>
                                                                    <Col span={2}>
                                                                        <Text strong type="secondary">{"X-axis"}</Text>
                                                                    </Col>
                                                                    <Col span={22}>
                                                                        <Slider
                                                                            key={"separateX"}
                                                                            range
                                                                            min={xMin}
                                                                            max={xMax}
                                                                            defaultValue={[xMin, xMax]}
                                                                            onAfterChange={(e) => {
                                                                                setRange((prevState: any) => ({ ...prevState, [x_res + y_res]: { ...prevState?.[x_res + y_res], x: { min: e?.[0], max: e?.[1] } } }))
                                                                            }}
                                                                        />
                                                                    </Col>
                                                                </Row>
                                                                <Row justify={"space-between"} align={"middle"}>
                                                                    <Col span={2}>
                                                                        <Text strong type="secondary">{"Y-axis"}</Text>
                                                                    </Col>
                                                                    <Col span={22}>
                                                                        <Slider
                                                                            key={"separateY"}
                                                                            range
                                                                            min={yMin}
                                                                            max={yMax}
                                                                            defaultValue={[yMin, yMax]}
                                                                            onAfterChange={(e) => {
                                                                                setRange((prevState: any) => ({ ...prevState, [x_res + y_res]: { ...prevState?.[x_res + y_res], y: { min: e?.[0], max: e?.[1] } } }))
                                                                            }}
                                                                        />
                                                                    </Col>
                                                                </Row>
                                                                {componentType === "crud" && getCalculationTable(x_res + y_res)}
                                                            </Card>
                                                        </Col>
                                                    )
                                                })
                                            )
                                        }))
                                    )}
                            </Space>
                        </>
                }
            </Space >
        )
    }, [tab, interpolationForm, inputCount, handleSubmit, t, interpolationOptions, selectedAxis.xAxis, formatter, parser, interPolationGraphsDataStatus, interPolationGraphsData?.results, selectedTrial, trialSetList, xaxis, yaxis, superimposed, plotsData, generateValues, handleCountChange, interpolationGraphConfigData, interpolationMinMaxData?.min, interpolationMinMaxData?.max, range, componentType, getCalculationTable, graphsClickEvent])


    return (
        <Spin spinning={plotsDataStatus === AsyncStates.LOADING || graphsDataDeleteStatus === AsyncStates.LOADING} indicator={<LoadingOutlined />}>
            <Space direction="vertical" size="large" style={{ width: "100%" }}>
                <Tabs
                    activeKey={tab}
                    onChange={(e) => {
                        setSuperimposed(false)
                        setXaxis([])
                        setYaxis([])
                        setTab(e)
                        dispatch(fetchInterpolationDataClear())
                    }}>
                    <Tabs.TabPane tab={"DSC"} key={"generalised_dsc"} tabKey={"generalised_dsc"}>
                        {getPlotsData()}
                    </Tabs.TabPane>
                    {Boolean(configs?.dma_graphs) &&
                        <Tabs.TabPane tab={"DMA"} key={"generalised_dma"} tabKey={"generalised_dma"}>
                            {getPlotsData()}
                        </Tabs.TabPane>
                    }
                    {Boolean(configs?.gpc_graphs) &&
                        <Tabs.TabPane tab={"GPC"} key={"generalised_gpc"} tabKey={"generalised_gpc"}>
                            {getPlotsData()}
                        </Tabs.TabPane>
                    }
                    {Boolean(configs?.interpolation_graphs) &&
                        <Tabs.TabPane tab={"Interpolation"} key={"interpolation"} tabKey={"Interpolation"}>
                            {getPlotsData()}
                        </Tabs.TabPane>
                    }
                </Tabs >
            </Space>
        </Spin>
    )
}



const InterpolationChart = ({ data, plotKey, xAxisName, yAxisName }: any) => {
    const { getValue: getEUValue } = useValue()
    const chartRef = useRef<HighchartsReact.RefObject>(null);
    const [plotType, setPlotType] = useState<'linear' | 'logarithmic'>('linear');

    const isSidebarCollapsed = useSelector((state: StoreState) => state.sidebar.collapsed)

    useEffect(() => {
        if (chartRef.current) {
            chartRef?.current?.chart?.redraw()
        }
    }, [isSidebarCollapsed, plotType])

    useEffect(() => {
        if (chartRef?.current?.chart) {
            chartRef.current.chart.zoomOut();
        }
    }, [plotType]);

    const getUniqueXAxisValues = useCallback((plotData: any = {}) => {
        const xAxisValues = new Set<number | null>();
        for (let method in plotData) {
            plotData?.[method]?.data?.forEach(([value]: [number | undefined]) =>
                xAxisValues.add(value ?? null)
            );
        }
        return [...xAxisValues].sort((a, b) => (a === null ? 1 : b === null ? -1 : a - b));
    }, [])


    const extractTableData = useCallback((plotData: any = {}) => {
        const xAxisValues = getUniqueXAxisValues(plotData);

        return xAxisValues.map((xAxisValue) => {
            let row: any = { key: getEUValue(xAxisValue), x_axis_value: getEUValue(xAxisValue) };

            for (let method in plotData) {
                const methodData = plotData?.[method]?.data ?? [];
                const methodName = plotData?.[method]?.name;
                const value = methodData.find((item: any) => item[0] === xAxisValue)?.[1] || null;
                row[methodName] = getEUValue(value)
            }
            return row;
        });
    }, [getUniqueXAxisValues, getEUValue])

    const getInterpolationChartOptions = useCallback((data: any, plotType = "linear") => {
        return {
            title: {
                text: null,
            },
            chart: {
                zoomType: "xy",
                events: {
                    load: function () {
                        (this as any)?.redraw();
                    },
                },
            },
            xAxis: {
                title: {
                    enabled: true,
                    text: xAxisName
                },
            },
            yAxis: {
                title: {
                    enabled: true,
                    text: yAxisName ?? data?.name,
                },
                type: plotType,
                ...(plotType === 'logarithmic' && {
                    custom: { allowNegativeLog: true },
                }),
            },
            credits: {
                enabled: false
            },
            tooltip: {
                useHTML: true,
                formatter: function (item: any) {
                    const point = (this as any).point
                    return `
                            <strong>${xAxisName}: ${getEUValue(point.x)}</strong><br/>
                            <strong>${point.series.name} : ${getEUValue(point.y)}</strong>`
                }
            },
            series: Object.keys(data?.plot_data)?.reduce((acc: any[], key) => {
                const curr = data?.plot_data?.[key]

                if (curr) {
                    acc.push({
                        key: key,
                        name: curr.name,
                        type: curr.type ?? "line",
                        data: [...(curr?.data ?? [])],
                        ...(key !== "original_data" ? {
                            marker: {
                                enabled: true,
                                radius: 10
                            }
                        } : {})
                    })
                }
                return acc
            }, [])
        }
    }, [getEUValue, xAxisName, yAxisName])


    const options = getInterpolationChartOptions(data, plotType);

    const getColumns = useCallback((data) => {
        return [
            {
                title: xAxisName,
                dataIndex: "x_axis_value",
                key: "x_axis_value",
                fixed: "left" as "left",
                width: 120,
            },
            ...Object.values(data).map((method: any) => ({
                title: method.name,
                dataIndex: method.name,
                key: method.name,
            })),
        ];
    }, [xAxisName])

    const filteredPlotData = Object.entries(data.plot_data ?? {}).reduce((acc, [k, v]) => {
        if (k !== 'original_data') acc[k] = v;
        return acc;
    }, {} as Record<string, any>);


    return <div
        style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '1rem',
        }}
    >
        <Radio.Group
            options={[
                { label: 'Linear', value: 'linear' },
                { label: 'Logarithmic', value: 'logarithmic' },
            ]}
            value={plotType || 'linear'}
            onChange={(e) => {
                setPlotType(e.target.value)
            }}
        />

        <HighchartsReact highcharts={Highcharts} options={{ ...options }} key={plotKey} ref={chartRef} />

        <Table
            columns={getColumns(filteredPlotData)}
            dataSource={extractTableData(filteredPlotData)}
            scroll={{ x: 'max-content' }}
        />
    </div>
}